/**
 * Time Countdown — real-time life remaining + year remaining.
 * Updates every second. Progress bars for year/month/week/day.
 */

import { memo } from 'react'
import { motion } from 'framer-motion'
import { Clock, Hourglass } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CountdownTile } from './countdown-tile'
import type { TimeState } from '@turnifly/shared/hooks'

interface TimeCountdownProps {
  timeState: TimeState
}

export const TimeCountdown = memo(function TimeCountdown({ timeState }: TimeCountdownProps) {
  const { t } = useTranslation()
  const { yearCountdown, lifeCountdown, yearProgress, monthProgress, weekProgress, dayProgress } = timeState

  return (
    <div className="space-y-3">
      {/* ── Life remaining ── */}
      {lifeCountdown && (
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]">
          <div className="absolute -top-20 -right-16 w-44 h-44 rounded-full bg-primary/8 blur-[80px] pointer-events-none" />

          <div className="relative p-4">
            <div className="flex items-center gap-2 mb-4">
              <Hourglass className="w-3.5 h-3.5 text-accent" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-hint">
                {t('life.lifeRemaining')}
              </span>
            </div>

            <div className="flex items-center justify-between px-1">
              <CountdownTile value={lifeCountdown.years} label={t('life.years')} accent="rgba(82,218,255,0.5)" large />
              <Sep />
              <CountdownTile value={lifeCountdown.months} label={t('life.months')} accent="rgba(0,97,255,0.5)" large />
              <Sep />
              <CountdownTile value={lifeCountdown.days} label={t('life.days')} accent="rgba(0,97,255,0.4)" />
              <Sep />
              <CountdownTile value={lifeCountdown.hours} label={t('life.hours')} accent="rgba(168,85,247,0.4)" />
              <Sep />
              <CountdownTile value={lifeCountdown.minutes} label={t('life.min')} accent="rgba(245,158,11,0.4)" />
              <Sep />
              <SecondsTile value={lifeCountdown.seconds} label={t('life.sec')} />
            </div>
          </div>
        </div>
      )}

      {/* ── Year remaining ── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]">
        <div className="relative p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-hint">
              {t('life.yearRemaining')}
            </span>
          </div>

          <div className="flex items-center justify-between px-1">
            <CountdownTile value={yearCountdown.days} label={t('life.days')} accent="rgba(0,97,255,0.4)" large />
            <Sep />
            <CountdownTile value={yearCountdown.hours} label={t('life.hours')} accent="rgba(82,218,255,0.4)" />
            <Sep />
            <CountdownTile value={yearCountdown.minutes} label={t('life.min')} accent="rgba(168,85,247,0.4)" />
            <Sep />
            <SecondsTile value={yearCountdown.seconds} label={t('life.sec')} />
          </div>
        </div>
      </div>

      {/* ── Progress bars ── */}
      <div className="grid grid-cols-2 gap-2">
        <ProgressCard label={t('life.yearProgress')} value={yearProgress} color="#0061FF" />
        <ProgressCard label={t('life.monthProgress')} value={monthProgress} color="#22C55E" />
        <ProgressCard label={t('life.weekProgress')} value={weekProgress} color="#A855F7" />
        <ProgressCard label={t('life.dayProgress')} value={dayProgress} color="#F59E0B" />
      </div>
    </div>
  )
})

// ── Seconds with pulsing red ──

function SecondsTile({ value, label }: { value: number; label: string }) {
  return (
    <div className="relative flex flex-col items-center gap-1">
      <div className="absolute -top-1 w-6 h-6 rounded-full bg-danger/25 blur-lg animate-pulse pointer-events-none" />
      <motion.span
        key={value}
        initial={{ opacity: 0.6, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.12 }}
        className="text-lg font-display font-extrabold tabular-nums leading-none text-danger"
      >
        {String(value).padStart(2, '0')}
      </motion.span>
      <span className="text-[9px] font-medium uppercase tracking-wider text-danger/60">{label}</span>
    </div>
  )
}

// ── Separator ──

function Sep() {
  return <span className="text-white/8 text-base font-light mb-3 select-none">:</span>
}

// ── Progress card ──

function ProgressCard({ label, value, color }: { label: string; value: number; color: string }) {
  const clamped = Math.min(value, 100)
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-medium text-hint uppercase tracking-wider">{label}</span>
        <span className="text-[11px] font-bold tabular-nums" style={{ color }}>
          {value.toFixed(1)}%
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${clamped}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  )
}
